import React, {
  KeyboardEvent,
  ChangeEvent,
  forwardRef,
  RefObject,
  useRef,
  useEffect,
} from 'react'
import { Textarea, TextareaProps, getTextareaClassName } from '@wellesley/ui'
import TextareaAutosize, {
  TextareaAutosizeProps,
} from 'react-textarea-autosize'

import AutocompleteField from '../AutocompleteField'
import { UseAutoCompleteProps } from '../AutocompleteField/AutocompleteRow/useAutocomplete'

const isRefObject = <T,>(ref: unknown): ref is RefObject<T> => {
  return ref !== null && typeof ref === 'object' && 'current' in ref
}

const asRefObjectOrNull = <T,>(ref: unknown): RefObject<T> | null => {
  return isRefObject<T>(ref) ? ref : null
}

type RichTextAreaProps = Omit<UseAutoCompleteProps, 'onChange' | 'inputRef'> & {
  text: string
  isError?: boolean
  inputRef?: React.RefObject<HTMLTextAreaElement>
  onChange(e: ChangeEvent<HTMLTextAreaElement>): void
  onBlur?(): void
  onFocus?(): void
  onKeyDown?(e: KeyboardEvent<HTMLTextAreaElement>): void
  id?: string
  name?: string
  isForm?: boolean
  disabled?: boolean
  rowsCount?: number
  autoFocus?: boolean
  className?: string
  placeholder?: string
  maxLength?: number
  userMentionSearchScope?: {
    groupId?: string
  }
}

const Field = forwardRef<
  HTMLTextAreaElement,
  TextareaProps & { isForm: boolean; isError?: boolean }
>(({ isForm, isError, autoFocus, rows, ...props }, ref) => {
  useEffect(() => {
    if (!ref || !autoFocus) return
    const timeoutId = setTimeout(() => {
      asRefObjectOrNull<HTMLTextAreaElement>(ref)?.current?.focus()
    }, 0)
    return () => {
      clearTimeout(timeoutId)
    }
  }, [autoFocus, ref])

  return isForm ? (
    <Textarea ref={ref} {...props} />
  ) : (
    <TextareaAutosize
      ref={ref}
      minRows={rows}
      cacheMeasurements
      className={getTextareaClassName(!!isError, props.className)}
      {...(props as TextareaAutosizeProps)}
    />
  )
})

Field.displayName = 'Field'

const RichTextArea = forwardRef<HTMLTextAreaElement, RichTextAreaProps>(
  (
    {
      text,
      isForm = false,
      rowsCount,
      isError,
      className,
      placeholder,
      inputRef: globalRef,
      autoFocus,
      setText,
      onBlur,
      onFocus,
      onKeyDown,
      onChange,
      name,
      id,
      maxLength,
      disabled,
      userMentionSearchScope,
    },
    ref,
  ) => {
    const localRef = useRef<HTMLTextAreaElement | null>(null)
    const textAreaRef =
      asRefObjectOrNull<HTMLTextAreaElement>(ref) || globalRef || localRef

    return (
      <AutocompleteField
        text={text}
        inputRef={textAreaRef}
        setText={setText}
        onChange={onChange}
        userMentionSearchScope={userMentionSearchScope}
        renderField={({
          handleScroll,
          handleChangeWithHashtagAutocomplete,
        }) => (
          <Field
            ref={textAreaRef}
            value={text}
            rows={rowsCount}
            autoFocus={autoFocus}
            isForm={isForm}
            onScroll={handleScroll}
            onKeyDown={onKeyDown}
            onFocus={onFocus}
            onBlur={onBlur}
            onChange={handleChangeWithHashtagAutocomplete}
            placeholder={placeholder ?? 'Type your message here...'}
            className={className}
            data-testid={name}
            name={name}
            id={id}
            disabled={disabled}
            isError={isError}
            maxLength={maxLength}
          />
        )}
      />
    )
  },
)

RichTextArea.displayName = 'RichTextArea'

export default RichTextArea
